import { displayMessage } from '@/helpers/helper'
import { i18n } from '@/i18n'
import axios from 'axios'

/**
 * @typedef {Object} ThemeColors
 * @property {string} text_color - Global text color
 * @property {string} primary_color - Primary color
 * @property {string} secondary_color - Secondary color
 * @property {string} drawer_color - Navigation drawer background color
 * @property {string} drawer_text_color - Navigation drawer text color
 * @property {string} data_table_header_color - Data table header background color
 */

/**
 * @typedef {Object} PartnerModules
 * @property {boolean} dashboard_access - Dashboard module access
 * @property {boolean} orders_access - Orders module access
 * @property {boolean} returns_access - Returns module access
 * @property {boolean} locations_access - Locations module access
 * @property {boolean} users_access - Users module access
 * @property {boolean} settings_access - Settings module access
 * @property {boolean} order_settings_access - Order settings module access
 * @property {boolean} customer_settings_access - Customer settings module access
 * @property {boolean} appointments_access - Appointments module access
 * @property {boolean} unified_stock_access - Unified stock module access
 * @property {boolean} reception_access - Reception module access
 * @property {boolean} logs_access - Logs module access
 * @property {boolean} parsed_files_access - Parsed files module access
 * @property {boolean} catalog_access - Catalog module access
 * @property {boolean} templates_manager_access - Templates manager module access
 * @property {boolean} parsings_history_access - Parsings history module access
 * @property {boolean} analytics_access - Analytics module access
 * @property {boolean} lookbook_manager_access - Lookbook manager module access
 * @property {boolean} carriers_access - Carriers module access
 * @property {Object} digital_in_store_access - Digital in-store module configuration
 * @property {boolean} digital_in_store_access.enabled - Whether digital in-store is enabled
 * @property {string} [digital_in_store_access.url] - Digital in-store URL
 * @property {boolean} digital_in_store_access.open_in_panel - Whether to open in panel
 * @property {boolean} digital_in_store_access.additional_services_access - Additional services access
 * @property {boolean} digital_in_store_access.configuration_manager_access - Configuration manager access
 * @property {Object} octipas_catalog_access - Octipas catalog module configuration
 * @property {boolean} octipas_catalog_access.enabled - Whether Octipas catalog is enabled
 * @property {string} [octipas_catalog_access.url] - Octipas catalog URL
 * @property {string} [octipas_catalog_access.default_store] - Default store identifier
 * @property {boolean} tasks_access - Tasks module access
 * @property {Object} [coheris_access] - Coheris module configuration
 * @property {boolean} coheris_access.enabled - Whether Coheris is enabled
 * @property {string} [coheris_access.url] - Coheris URL
 * @property {boolean} checkout_tunnel_access - Checkout tunnel module access
 */

/**
 * @typedef {Object} PartnerCountrySettings
 * @property {string} [partner_logo] - Partner logo URL
 * @property {Object} [theme] - Theme configuration
 * @property {ThemeColors} [theme.light] - Light theme colors
 * @property {ThemeColors} [theme.dark] - Dark theme colors
 * @property {string} [font] - Font family (lato, source_sans_pro, arial, etc.)
 * @property {PartnerModules} [modules] - Partner modules configuration
 */

/**
 * @typedef {Object} PartnerSettings
 * @property {PartnerCountrySettings} default - Default settings for the partner
 * @property {Object.<string, PartnerCountrySettings>} [additionalProperties] - Country-specific overrides
 */

/**
 * @typedef {Object} PartnerResponse
 * @property {string} partner_id - Partner unique identifier
 * @property {string} name - Partner name
 * @property {string} [tenant_id] - Tenant identifier
 * @property {PartnerSettings} settings - Partner settings and configuration
 */

/**
 * @typedef {Object} PartnerResponseAdmin
 * @property {string} partner_id - Partner unique identifier
 * @property {string} name - Partner name
 * @property {string} [tenant_id] - Tenant identifier
 * @property {PartnerSettings} settings - Partner settings and configuration
 * @property {string} [api_key] - API key for the partner
 * @property {string} [eresa_key] - Eresa key for the partner
 * @property {string} [checkout_key] - Checkout key for the partner
 */

/**
 * @typedef {Object} AuthProvider
 * @property {string} accessToken - The bearer token used for API authentication
 * @property {number} expiresAt - Token expiration timestamp in milliseconds
 * @property {Function} isAuthenticated - Method to refresh authentication status
 */

/**
 * Service for interacting with the Partner Service API.
 * Provides methods for partner configuration retrieval and management.
 *
 * @class PartnerService
 * @example
 * const partnerService = new PartnerService(baseUrl, authProvider);
 * const currentPartner = await partnerService.getCurrentPartner();
 */
export class PartnerService {
  /**
   * Authentication provider instance
   * @type {AuthProvider}
   */
  auth

  /**
   * Current access token for API requests
   * @type {string}
   */
  accessToken

  /**
   * Promise tracking ongoing token refresh operation
   * @type {Promise<void>|null}
   */
  tokenRefreshing

  /**
   * Axios instance configured for partner API requests
   * @type {import('axios').AxiosInstance}
   */
  partnerAPI

  /**
   * Creates a new instance of the PartnerService.
   *
   * Initializes the service with the provided authentication provider.
   * Sets up an Axios instance with request/response interceptors for automatic token refresh
   * and error handling.
   *
   * @constructor
   * @param {AuthProvider} auth - The authentication provider containing the access token
   * @throws {Error} Throws an error if the base URL is missing or auth is invalid
   *
   * @example
   * const authProvider = {
   *   accessToken: 'bearer_token',
   *   expiresAt: Date.now() + 3600000,
   *   isAuthenticated: async () => { ... }
   * };
   * const service = new PartnerService(authProvider);
   */
  constructor(auth) {
    this.auth = auth
    this.accessToken = auth?.accessToken
    this.tokenRefreshing = null

    if (!auth?.accessToken) {
      this.emitError('missing_auth', true)
    }

    // Initialize the Axios HTTP client with interceptors
    this.partnerAPI = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: 29900,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
    })

    // Request interceptor for automatic token refresh
    this.partnerAPI.interceptors.request.use(
      (config) => this.refreshTokenIfNeeded(config),
      (error) => Promise.reject(error)
    )

    // Response interceptor for centralized error handling
    this.partnerAPI.interceptors.response.use(
      (response) => response,
      (error) => {
        this.emitError(error?.response?.data?.message || 'api_error')
        console.error('Partner API:', error)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Refreshes the access token if it's expired or about to expire before making API requests.
   * Prevents concurrent token refresh operations by using a shared promise.
   *
   * @private
   * @param {import('axios').AxiosRequestConfig} config - Axios request configuration object
   * @returns {Promise<import('axios').AxiosRequestConfig>} Updated configuration with fresh token
   *
   * @example
   * // This method is called automatically by the request interceptor
   * // No direct usage required
   */
  async refreshTokenIfNeeded(config) {
    const now = Date.now()

    // Check if token is still valid (with 30 second buffer before token expires)
    if (this.auth.expiresAt - 30000 > now) {
      return config
    }

    // Prevent concurrent token refreshes
    if (!this.tokenRefreshing) {
      this.tokenRefreshing = this.refreshToken()
    }

    await this.tokenRefreshing
    config.headers.Authorization = `Bearer ${this.accessToken}`
    return config
  }

  /**
   * Refreshes the authentication token by calling the auth provider's authentication method.
   * Updates the access token and Axios default headers with the new token.
   *
   * @private
   * @returns {Promise<void>} Promise that resolves when token refresh is complete
   * @throws {Error} Throws if token refresh fails
   *
   * @example
   * // This method is called automatically when token expires
   * // No direct usage required
   */
  async refreshToken() {
    try {
      await this.auth.isAuthenticated()
      this.accessToken = this.auth.accessToken
      this.partnerAPI.defaults.headers.Authorization = `Bearer ${this.accessToken}`
    } finally {
      this.tokenRefreshing = null
    }
  }

  /**
   * Displays error messages through the application's message system and optionally throws an error.
   * Uses internationalization for localized error messages.
   *
   * @private
   * @param {string} messageKey - Translation key for the error message in the partner namespace
   * @param {boolean} [withThrow=false] - Whether to throw an Error after displaying the message
   * @throws {Error} Throws an error with the localized message if withThrow is true
   *
   * @example
   * // Display error message only
   * this.emitError('api_error');
   *
   * // Display error message and throw exception
   * this.emitError('missing_base_url', true);
   */
  emitError(messageKey, withThrow = false) {
    const text = i18n.te(`partner.${messageKey}`) ? i18n.t(`partner.${messageKey}`) : messageKey
    displayMessage(text)
    if (withThrow) {
      throw new Error(text)
    }
  }

  /**
   * Makes a GET request to the partner API with automatic parameter cleaning.
   * Removes null and undefined parameters before sending the request.
   *
   * @private
   * @param {string} path - API endpoint path relative to the base URL
   * @param {Object} [params={}] - Query parameters object
   * @returns {Promise<any>} Promise resolving to the API response data
   * @throws {Error} Throws if the API request fails
   *
   * @example
   * const data = await this.get('/partners/1');
   */
  async get(path, params = {}) {
    // Remove undefined/null parameters
    const cleanedParams = Object.fromEntries(
      // eslint-disable-next-line no-unused-vars
      Object.entries(params).filter(([_key, value]) => ![null, undefined].includes(value))
    )

    const response = await this.partnerAPI.get(path, { params: cleanedParams })
    return response.data
  }

  /**
   * Makes a POST request to the partner API.
   *
   * @private
   * @param {string} path - API endpoint path relative to the base URL
   * @param {Object} data - Request body data
   * @returns {Promise<any>} Promise resolving to the API response data
   * @throws {Error} Throws if the API request fails
   *
   * @example
   * const result = await this.post('/partners', partnerData);
   */
  async post(path, data) {
    const response = await this.partnerAPI.post(path, data)
    return response.data
  }

  /**
   * Makes a PUT request to the partner API.
   *
   * @private
   * @param {string} path - API endpoint path relative to the base URL
   * @param {Object} data - Request body data
   * @returns {Promise<any>} Promise resolving to the API response data
   * @throws {Error} Throws if the API request fails
   *
   * @example
   * const result = await this.put('/partners/1', updatedPartnerData);
   */
  async put(path, data) {
    const response = await this.partnerAPI.put(path, data)
    return response.data
  }

  /**
   * Retrieves the current partner configuration.
   * Returns the partner settings and module access rights for the authenticated user.
   *
   * @returns {Promise<PartnerResponse>} Promise resolving to current partner configuration
   * @throws {Error} Throws if the API request fails or user is unauthorized
   *
   * @example
   * const partner = await service.getCurrentPartner();
   * console.log(partner.name, partner.settings.default.modules);
   */
  async getCurrentPartner() {
    return this.get('/v1/partners/current')
  }

  /**
   * Retrieves all partners with optional filtering, pagination and sorting.
   * Only users with role 'octipas-admin' can perform this operation.
   *
   * @param {Object} [options={}] - Query options for filtering, pagination and sorting
   * @param {string} [options.partner_id] - Filter by partner ID
   * @param {string} [options.name] - Filter by partner name
   * @param {string} [options.tenant_id] - Filter by tenant ID
   * @param {number} [options.itemsPerPage] - Number of items per page for pagination
   * @param {number} [options.page] - Page number for pagination
   * @param {string[]} [options.sortBy] - Array of field names to sort by
   * @param {(boolean|string)[]} [options.sortDesc] - Array of sort directions (true/false or 'true'/'false' strings)
   * @returns {Promise<PartnerResponseAdmin[]>} Promise resolving to array of all partners with admin details
   * @throws {Error} Throws if the API request fails, user is unauthorized, or user lacks admin role
   *
   * @example
   * // Get all partners
   * const allPartners = await service.getAllPartners();
   *
   * // Get partners with filtering
   * const filtered = await service.getAllPartners({ name: 'Octipas' });
   *
   * // Get partners with pagination
   * const paginated = await service.getAllPartners({ page: 1, itemsPerPage: 10 });
   *
   * // Get partners with sorting
   * const sorted = await service.getAllPartners({
   *   sortBy: ['name', 'partner_id'],
   *   sortDesc: [false, true]
   * });
   *
   * // Combined filtering, pagination and sorting
   * const results = await service.getAllPartners({
   *   tenant_id: 'octipas',
   *   page: 2,
   *   itemsPerPage: 20,
   *   sortBy: ['name'],
   *   sortDesc: [false]
   * });
   */
  async getAllPartners(options = {}) {
    const params = {
      partner_id: options?.partner_id,
      name: options?.name,
      tenant_id: options?.tenant_id,
      itemsPerPage: options?.itemsPerPage,
      page: options?.page,
      sortBy: options?.sortBy,
      sortDesc: options?.sortDesc,
    }
    return this.get('/v1/partners', params)
  }

  /**
   * Retrieves a specific partner by ID.
   * Only users with role 'octipas-admin' can perform this operation.
   *
   * @param {number} partnerId - The unique partner identifier
   * @returns {Promise<PartnerResponseAdmin>} Promise resolving to partner details with admin information
   * @throws {Error} Throws if partnerId is invalid, partner not found, user unauthorized, or lacks admin role
   *
   * @example
   * const partner = await service.getPartnerById(1);
   * console.log(partner.name, partner.tenant_id);
   */
  async getPartnerById(partnerId) {
    if (!partnerId || partnerId?.trim()?.length === 0) {
      this.emitError('invalid_partner_id', true)
    }
    return this.get(`/v1/partners/${partnerId}`)
  }

  /**
   * Creates a new partner.
   * Only users with role 'octipas-admin' can perform this operation.
   *
   * @param {PartnerResponseAdmin} partnerData - The partner data to create
   * @returns {Promise<any>} Promise resolving when partner is created successfully
   * @throws {Error} Throws if partner data is invalid, user unauthorized, or lacks admin role
   *
   * @example
   * const newPartner = {
   *   name: 'New Partner',
   *   tenant_id: 'new-tenant',
   *   settings: {
   *     default: {
   *       modules: { dashboard_access: true }
   *     }
   *   }
   * };
   * await service.createPartner(newPartner);
   */
  async createPartner(partnerData) {
    if (!partnerData?.name) {
      this.emitError('partner_name_required', true)
    }

    if (!partnerData?.settings) {
      this.emitError('partner_settings_required', true)
    }

    return this.post('/v1/partners', partnerData)
  }

  /**
   * Updates an existing partner.
   * Only users with role 'octipas-admin' can perform this operation.
   *
   * @param {number} partnerId - The unique partner identifier
   * @param {PartnerResponseAdmin} partnerData - The updated partner data
   * @returns {Promise<any>} Promise resolving when partner is updated successfully
   * @throws {Error} Throws if partnerId invalid, partner not found, data invalid, user unauthorized, or lacks admin role
   *
   * @example
   * const updatedData = {
   *   name: 'Updated Partner Name',
   *   settings: {
   *     default: {
   *       modules: { analytics_access: true }
   *     }
   *   }
   * };
   * await service.updatePartner(1, updatedData);
   */
  async updatePartner(partnerId, partnerData) {
    if (!partnerId || partnerId?.trim()?.length === 0) {
      this.emitError('invalid_partner_id', true)
    }

    if (!partnerData?.name) {
      this.emitError('partner_name_required', true)
    }

    if (!partnerData?.settings) {
      this.emitError('partner_settings_required', true)
    }

    return this.put(`/v1/partners/${partnerId}`, partnerData)
  }
}
